// ============================================================================
// 📦 VERSION: 2025.12.04.0001
// ============================================================================
// CHANGELOG:
// 2025.12.04.0001 - Initial creation - Unclaimed Rewards Badge
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getUnclaimedRewards } from '../rewardsApi';

/**
 * UnclaimedRewardsBadge - Shows notification when player has rewards to claim
 * 
 * Usage:
 * <UnclaimedRewardsBadge 
 *   isAuthenticated={isLoggedIn}
 *   onClick={() => setShowClaimModal(true)}
 *   refreshTrigger={someStateToTriggerRefresh}
 * />
 */
const UnclaimedRewardsBadge = ({ 
  isAuthenticated = false,
  onClick,
  refreshTrigger,  // Change this prop to trigger a refresh
  position = 'bottom-right',  // 'bottom-right', 'top-right', 'inline'
  size = 'normal'  // 'small', 'normal', 'large'
}) => {
  const [count, setCount] = useState(0);
  const [totals, setTotals] = useState({ traks: 0, tags: 0 });
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);

  // Size configurations
  const sizeConfig = {
    small: {
      button: 'w-12 h-12 text-lg',
      badge: 'w-5 h-5 text-xs',
      tooltip: 'text-xs'
    },
    normal: {
      button: 'w-16 h-16 text-2xl',
      badge: 'w-6 h-6 text-sm',
      tooltip: 'text-sm'
    },
    large: {
      button: 'w-20 h-20 text-3xl',
      badge: 'w-7 h-7 text-base',
      tooltip: 'text-base'
    }
  };

  // Position configurations
  const positionConfig = {
    'bottom-right': 'fixed bottom-24 right-6',
    'top-right': 'fixed top-20 right-6',
    'inline': 'relative'
  };

  const config = sizeConfig[size] || sizeConfig.normal;
  const posClass = positionConfig[position] || positionConfig['bottom-right'];

  // Load unclaimed count
  const loadUnclaimedCount = useCallback(async () => {
    if (!isAuthenticated) {
      setCount(0);
      setLoading(false);
      return;
    }

    try {
      const data = await getUnclaimedRewards();
      if (data?.success) {
        const newCount = data.totals?.count || 0;
        
        // Trigger pulse animation when count increases
        if (newCount > count && count > 0) {
          setIsPulsing(true);
          setTimeout(() => setIsPulsing(false), 1000);
        }
        
        setCount(newCount);
        setTotals({
          traks: data.totals?.traks || 0,
          tags: data.totals?.tags || 0
        });
      }
    } catch (error) {
      console.error('Failed to load unclaimed count:', error);
    }
    setLoading(false);
  }, [isAuthenticated, count]);

  // Initial load
  useEffect(() => {
    loadUnclaimedCount();
  }, [loadUnclaimedCount]);

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      loadUnclaimedCount();
    }
  }, [refreshTrigger, loadUnclaimedCount]);

  // Poll every 30 seconds for new rewards
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(loadUnclaimedCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, loadUnclaimedCount]);

  // Show/hide with animation
  useEffect(() => {
    if (count > 0) {
      setIsVisible(true);
    } else {
      // Delay hiding to allow animation
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [count]);

  // Don't render if not authenticated or no rewards
  if (!isAuthenticated || (!isVisible && count === 0)) {
    return null;
  }

  return (
    <div className={`${posClass} z-40`}>
      {/* Tooltip on hover */}
      <div className={`
        absolute bottom-full right-0 mb-2 
        bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl
        opacity-0 group-hover:opacity-100 transition-opacity
        pointer-events-none whitespace-nowrap border border-gray-700
        ${config.tooltip}
      `}>
        <div className="font-bold mb-1">🎁 {count} reward{count !== 1 ? 's' : ''} waiting!</div>
        <div className="flex gap-3 text-gray-300">
          {totals.traks > 0 && <span>🏆 +{totals.traks} TRAKs</span>}
          {totals.tags > 0 && <span>🎯 +{totals.tags} tags</span>}
        </div>
        {/* Arrow */}
        <div className="absolute top-full right-4 border-8 border-transparent border-t-gray-900" />
      </div>

      {/* Main Button */}
      <button
        onClick={onClick}
        className={`
          group relative
          ${config.button}
          bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500
          hover:from-purple-500 hover:via-pink-400 hover:to-orange-400
          rounded-full shadow-2xl
          flex items-center justify-center
          transition-all duration-300
          hover:scale-110 hover:shadow-purple-500/50
          ${count > 0 ? 'animate-subtle-bounce' : ''}
          ${isPulsing ? 'animate-pulse-grow' : ''}
          ${count === 0 ? 'opacity-0 scale-75' : 'opacity-100 scale-100'}
        `}
        disabled={loading}
      >
        {/* Glow effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 blur-lg opacity-60 group-hover:opacity-80 transition-opacity" />
        
        {/* Icon */}
        <span className="relative z-10">
          {loading ? '⏳' : '🎁'}
        </span>

        {/* Count Badge */}
        {count > 0 && (
          <div className={`
            absolute -top-1 -right-1
            ${config.badge}
            bg-red-500 rounded-full
            flex items-center justify-center
            font-bold text-white
            shadow-lg
            animate-pop-in
            ${isPulsing ? 'animate-ping-once' : ''}
          `}>
            {count > 9 ? '9+' : count}
          </div>
        )}

        {/* Shine effect */}
        <div className="absolute inset-0 rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        </div>
      </button>

      {/* CSS Animations */}
      <style>{`
        @keyframes subtle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        
        @keyframes pulse-grow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        
        @keyframes pop-in {
          0% { transform: scale(0); }
          70% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        
        @keyframes ping-once {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .animate-subtle-bounce {
          animation: subtle-bounce 2s ease-in-out infinite;
        }
        
        .animate-pulse-grow {
          animation: pulse-grow 0.5s ease-in-out;
        }
        
        .animate-pop-in {
          animation: pop-in 0.3s ease-out;
        }
        
        .animate-ping-once {
          animation: ping-once 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

/**
 * Inline Badge - For use in navigation/headers
 * Just shows the count, no floating behavior
 */
export const InlineRewardsBadge = ({ 
  isAuthenticated = false,
  onClick,
  refreshTrigger
}) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setCount(0);
      setLoading(false);
      return;
    }

    try {
      const data = await getUnclaimedRewards();
      if (data?.success) {
        setCount(data.totals?.count || 0);
      }
    } catch (error) {
      console.error('Failed to load unclaimed count:', error);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    loadCount();
  }, [loadCount, refreshTrigger]);

  if (!isAuthenticated || count === 0) return null;

  return (
    <button
      onClick={onClick}
      className="relative flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 rounded-full text-white font-bold text-sm transition-all hover:scale-105"
    >
      <span>🎁</span>
      <span>{count}</span>
      {loading && <span className="animate-spin ml-1">⏳</span>}
    </button>
  );
};

export default UnclaimedRewardsBadge;
